// src/components/store/ProductCard.js
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import "../../static/storecss/ProductCard.css";

export default function ProductCard({ product }) {
  const navigate = useNavigate();
  const { items, addItem, ready } = useCart();
  const { isLoggedIn } = useAuth();

  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);

  const inStock = product.in_stock ?? product.stock > 0;
  const lowStock = inStock && product.stock > 0 && product.stock <= 5;
  const price = Number(product.price).toLocaleString();

  const imageSrc =
    product.primary_image ||
    product.images?.[0]?.image_url ||
    product.images?.[0]?.image ||
    null;

  const cartLine = items.find((i) => i.product_id === product.id);
  const alreadyInCart = Boolean(cartLine);

  const handleAdd = async (e) => {
    // Stop the click from bubbling up into the parent <Link>
    e.preventDefault();
    e.stopPropagation();

    if (!inStock || adding || alreadyInCart) return;

    if (!isLoggedIn) {
      navigate(`/login?next=/store/product/${product.slug}`);
      return;
    }

    setAdding(true);
    try {
      await addItem(product, 1);
      setAdded(true);
      setTimeout(() => setAdded(false), 1500);
    } catch (err) {
      
    } finally {
      setAdding(false);
    }
  };

  // Don't show the button until cart state is resolved
  const showAddButton = ready;

  return (
    <Link to={`/store/product/${product.slug}`} className="product-card">
      {/* ── Image ── */}
      <div className="product-card-image">
        {imageSrc ? (
          <img src={imageSrc} alt={product.name} loading="lazy" />
        ) : (
          <div className="product-card-placeholder">
            <i className="fa-solid fa-image" />
          </div>
        )}

        {!inStock && (
          <span className="product-card-badge badge-out">Out of stock</span>
        )}
        {lowStock && (
          <span className="product-card-badge badge-low">
            Only {product.stock} left
          </span>
        )}

        {/* ── Hover overlay ── */}
        <div className="product-card-overlay">
          <span className="product-card-overlay-btn">
            <i className="fa-regular fa-eye" />
            View details
          </span>
        </div>
      </div>

      {/* ── Info ── */}
      <div className="product-card-info">
        <h3 className="product-card-name">{product.name}</h3>

        <p className="product-card-price">
          <span className="product-card-currency">KES</span>
          {price}
        </p>

        {inStock ? (
          <p className="product-card-stock in">
            In stock: {product.stock}{" "}
            {product.stock === 1 ? "piece" : "pieces"}
          </p>
        ) : (
          <p className="product-card-stock out">Out of stock</p>
        )}

        {showAddButton && (
          <button
            type="button"
            className={`product-card-add ${
              alreadyInCart ? "is-in-cart" : ""
            } ${added ? "is-added" : ""}`}
            onClick={handleAdd}
            disabled={!inStock || adding || alreadyInCart}
            aria-label={
              alreadyInCart
                ? `${product.name} is already in your cart`
                : `Add ${product.name} to cart`
            }
          >
            {alreadyInCart ? (
              <>
                <i className="fa-solid fa-check" /> In cart
              </>
            ) : added ? (
              <>
                <i className="fa-solid fa-check" /> Added
              </>
            ) : (
              <>
                <i className="fa-solid fa-cart-shopping" />{" "}
                {adding ? "Adding…" : "Add to cart"}
              </>
            )}
          </button>
        )}
      </div>
    </Link>
  );
}